import { createAccessibilityPlugin } from 'chessboard/plugins/accessibility';
import { createAnalysisOverlayPlugin } from 'chessboard/plugins/analysis-overlay';
import { createArrowKeysPlugin } from 'chessboard/plugins/arrow-keys';
import { createArrowsPlugin } from 'chessboard/plugins/arrows';
import { createAssetsPlugin } from 'chessboard/plugins/assets';
import { createBlinkingHighlightPlugin } from 'chessboard/plugins/blinking-highlight';
import { createBoardDisabledPlugin } from 'chessboard/plugins/board-disabled';
import { createBoardSizePlugin } from 'chessboard/plugins/board-size';
import { createCheatDetectionPlugin } from 'chessboard/plugins/cheat-detection';
import { createCoordinatesPlugin } from 'chessboard/plugins/coordinates';
import { createCustomItemsPlugin } from 'chessboard/plugins/custom-items';
import { createDebugPlugin } from 'chessboard/plugins/debug';
import { createEffectsPlugin } from 'chessboard/plugins/effects';
import { createFadeSetupPlugin } from 'chessboard/plugins/fade-setup';
import { createFlipBoardPlugin } from 'chessboard/plugins/flip-board';
import { createFpsPlugin } from 'chessboard/plugins/fps';
import { createHighlightsPlugin } from 'chessboard/plugins/highlights';
import { createHoverSquarePlugin } from 'chessboard/plugins/hover-square';
import { createMarkingsPlugin } from 'chessboard/plugins/markings';
import { createMoveHintsPlugin } from 'chessboard/plugins/move-hints';
import { createPiecesPlugin } from 'chessboard/plugins/pieces';
import { createRendererErrorCatcherPlugin } from 'chessboard/plugins/renderer-error-catcher';
import { createSoundsPlugin } from 'chessboard/plugins/sounds';
import { createTimeControlPlugin } from 'chessboard/plugins/time-control';
import { createKeysPlugin } from 'chessboard/plugins/keys';
import { createHotkeysPlugin } from 'chessboard/plugins/hotkeys';
import { createWebGLContextLostPlugin } from 'chessboard/plugins/webgl-context-lost';
import { createRecorderPlugin } from 'chessboard/plugins/recorder';
import { createVideoSyncPlugin } from 'chessboard/plugins/video-sync';

// Highlights modules
import { getAnalysisHighlights } from 'chessboard/plugins/highlights/modules/analysis';
import { getMoveHighlights } from 'chessboard/plugins/highlights/modules/move';
import { getSelectionSquare } from 'chessboard/plugins/highlights/modules/selection';
import { Accessibility } from 'chessboard/plugins/accessibility/config/types';
import { BoardDisabled } from 'chessboard/plugins/board-disabled/config/types';
import { FlipBoard } from 'chessboard/plugins/flip-board/config/types';
import { Keys } from 'chessboard/plugins/keys/config/types';
import { Markings } from 'chessboard/plugins/markings/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import {
  ArrowKeys,
  Arrows,
  BlinkingHighlight,
  Coordinates,
  Highlights,
  HoverSquare,
  MoveHints,
  Sounds,
} from './types';

export {
  createAnalysisOverlayPlugin,
  createArrowKeysPlugin,
  createArrowsPlugin,
  createAssetsPlugin,
  createBlinkingHighlightPlugin,
  createBoardDisabledPlugin,
  createBoardSizePlugin,
  createCheatDetectionPlugin,
  createCoordinatesPlugin,
  createCustomItemsPlugin,
  createDebugPlugin,
  createEffectsPlugin,
  createFadeSetupPlugin,
  createFlipBoardPlugin,
  createFpsPlugin,
  createHighlightsPlugin,
  createHoverSquarePlugin,
  createMoveHintsPlugin,
  createPiecesPlugin,
  createRendererErrorCatcherPlugin,
  createSoundsPlugin,
  createTimeControlPlugin,
  createKeysPlugin,
  createHotkeysPlugin,
  createWebGLContextLostPlugin,
  createRecorderPlugin,
  createVideoSyncPlugin,
};

/**
 * Plugins that modify behavior or require client-specific parameters, and are therefore
 * not included in the plugin packages below.
 */
export { createEcoPlugin } from 'chessboard/plugins/eco';
export { createPregamePlugin } from 'chessboard/plugins/pregame';
export { createPremovesPlugin } from 'chessboard/plugins/premoves';
export { createReportingPlugin } from 'chessboard/plugins/reporting';
export { createMoveListPlugin } from 'chessboard/plugins/move-list';
export { createVerticalMoveListPlugin } from 'chessboard/plugins/vertical-move-list';
export { createHorizontalMoveListPlugin } from 'chessboard/plugins/horizontal-move-list';
export { createBoardImagesPlugin } from 'chessboard/plugins/board-images';

/**
 * The minimal set of plugins to create a working chessboard.
 */
export function createMinimalPlugins() {
  return [
    createAssetsPlugin(),
    createBoardSizePlugin(),
    createPiecesPlugin(),
    createRendererErrorCatcherPlugin(),
  ];
}

/**
 * The "standard" package includes all the basic functionality that is required on
 * chess.com (blinking squares, coordinates, hover square, move hints, sounds, etc.).
 */
export function createStandardPlugins({ exclude }: { exclude?: string[] } = {}) {
  const plugins = {
    [Keys.Plugin.Name]: createKeysPlugin,
    [Accessibility.Plugin.Name]: createAccessibilityPlugin,
    [ArrowKeys.Plugin.Name]: createArrowKeysPlugin,
    [Arrows.Plugin.Name]: createArrowsPlugin,
    [BlinkingHighlight.Plugin.Name]: createBlinkingHighlightPlugin,
    [Coordinates.Plugin.Name]: createCoordinatesPlugin,
    [FlipBoard.Plugin.Name]: createFlipBoardPlugin,
    [Highlights.Plugin.Name]: createHighlightsPlugin,
    [HoverSquare.Plugin.Name]: createHoverSquarePlugin,
    [Markings.Plugin.Name]: createMarkingsPlugin,
    [BoardDisabled.Plugin.Name]: createBoardDisabledPlugin,
    [MoveHints.Plugin.Name]: createMoveHintsPlugin,
    [Sounds.Plugin.Name]: createSoundsPlugin,
  };

  if (exclude) {
    exclude.forEach((plugin) => {
      delete plugins[plugin];
    });
  }

  const highlightParams = [getSelectionSquare, getMoveHighlights, getAnalysisHighlights];
  const pluginArray: Plugins.Model[] = [...createMinimalPlugins()];

  Object.keys(plugins).forEach((pluginName) => {
    if (pluginName === Highlights.Plugin.Name) {
      return pluginArray.push(plugins[pluginName](highlightParams));
    }

    return pluginArray.push(plugins[pluginName]());
  });

  return pluginArray;
}

/**
 * The "premium" package is a superset of the standard package including extra features that are common but not used on every page
 */
export function createPremiumPlugins() {
  return [
    ...createStandardPlugins(),
    createCheatDetectionPlugin(),
    createCustomItemsPlugin(),
    createEffectsPlugin(),
    createFadeSetupPlugin(),
  ];
}

/**
 * The standard package plus all the extras like effects, custom items, etc.
 */
export function createKitchenSinkPlugins() {
  return [
    ...createPremiumPlugins(),
    createAnalysisOverlayPlugin(),
    createDebugPlugin(),
    createRecorderPlugin(),
    createVideoSyncPlugin(),
  ];
}
